import React, {RefObject} from "react";
import {Drawer, Tabs, Tooltip} from "antd";
import {connect} from "react-redux";
import {getTranslate, Translate, TranslateFunction} from "react-localize-redux";
import {Nullable} from "@data-force/app";
import StoreState from "../../interfaces/store/storeState";
import "./JobDrawer.scss";
import {DestroyJobDrawerAction} from "../../interfaces/store/actions/job";
import {destroyJobDrawer} from "../../store/actions/job";
import TaskStatus from "./TaskStatus/TaskStatus";
import TaskDetails from "./TaskDetails/TaskDetails";
import {JobEntity} from "../../interfaces/store/actions/payload/job";
import {JobType} from "../../enums/enums";
import {formatString} from "../../utils/common";

interface StateProps {
    visible: boolean
    current: JobEntity
    activeJobType: string
    translate: TranslateFunction
    isMobile: boolean
    activeTab: string
    focusedElement: Nullable<RefObject<HTMLElement>>
}

interface DispatchProps {
    destroyJobDrawer(): DestroyJobDrawerAction
}

type Props = StateProps & DispatchProps;

const JobDrawer: React.FC<Props> = (props: Props): React.ReactElement => {
    const {
        visible, isMobile, activeJobType, translate, current: {name = "", title = ""}, activeTab, focusedElement,
    }: Props = props;
    const translatePath: string = "components.JobDrawer.JobDrawer";
    const titleFormat: string = translate(`${translatePath}.titleFormat.text`) as string;
    const formattedTitle:string = formatString(titleFormat, name, title);

    /**
     * Prevent context menu opening on long press only on mobile
     * @param {React.MouseEvent} e
     */
    const preventContextMenu = (e:React.MouseEvent): void => {
        if (isMobile) {
            e.preventDefault();
        }
    };

    /**
     * Closing Job Drawer and restoring focus to last focused job item
     */
    const onCloseHandler = (): void => {
        if (focusedElement) {
            (focusedElement as RefObject<HTMLElement>).current?.focus();
        }
        props.destroyJobDrawer();
    };

    return (
        <div
            onContextMenu={preventContextMenu}
        >
            <Drawer
                className="df-job-drawer"
                destroyOnClose
                onClose={onCloseHandler}
                maskClosable
                visible={visible}
                title={(
                    <Tooltip title={formattedTitle} placement="bottomLeft">
                        <strong>{formattedTitle}</strong>
                    </Tooltip>
                )}

            >
                <Tabs
                    type="card"
                    defaultActiveKey={activeTab}
                    moreIcon={(
                        <span aria-label={translate(`${translatePath}.moreTabs.text`) as string}>
                            <Translate id={`${translatePath}.moreTabsContent.text`}/>
                        </span>
                    )}
                >
                    {(activeJobType === JobType.IN_PROGRESS || activeJobType === JobType.COMPLETED) && (
                        <TaskStatus
                            key="task-status"
                            tab={translate(`${translatePath}.taskStatusTabTitle.text`) as string}
                        />
                    )}

                    <TaskDetails
                        key="task-details"
                        tab={translate(`${translatePath}.taskDetailsTabTitle.text`) as string}
                    />
                </Tabs>
            </Drawer>
        </div>
    );
};

function mapStateToProps(state: StoreState): StateProps {
    return {
        visible: state.job.isDrawerVisible,
        current: state.job.selectedJob || {} as JobEntity,
        translate: getTranslate(state.localize),
        activeJobType: state.job.activeJobType,
        isMobile: state.mobile.isMobile,
        activeTab: state.job.activeJobDrawerTab,
        focusedElement: state.focusElement.focusedElement,
    };
}

const mapDispatchToProps: DispatchProps = {
    destroyJobDrawer,
};

export default connect(mapStateToProps, mapDispatchToProps)(JobDrawer);
